import React, {useEffect, useState} from "react";
import {Tooltip} from "antd";
import {useTranslation} from "react-i18next";
import {useFetch} from "../../../../../hooks/useFetch";
import {useStore} from "../../../../../hooks/useStore";
import {arrayAlphabeticalSort} from "../../../../../utils/helpers";
import {SelectField} from "./SubCompanyInput.styles";
import turkishToEnglish from "../../../../../constants/turkishToEnglish";

const SubCompanyInput = ({
    value,
    onChange,
    onSelectedSubCompaniesChange = () => {},
    onFilteredCountChange = () => {},
    company,
    companies,
    disabled = true,
    initialCall = false,
    mode = undefined,
    showAllOption = false,
    allowClear = false,
    loadWithDelay = 0,
    city = undefined,
    activity = "enabled", // all, enabled, disabled
    optionFilter = () => {
        return true;
    },
    optionDisabled = () => false,
    optionDisabledTooltip = "",
    showLabel = true,
    marginBottom = "20px",
    ...props
}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [delayLoading, setDelayLoading] = useState(false);
    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            companies: companies?.join(".") || "",
            city: city,
            limit: 5000,
            activity: activity
        }),
        dependencies: [company, companies, city],
        initialCall: initialCall,
        condition: company !== "pool",
        callbackFunc: async response => {
            if (response?.rows?.length > 0 && stores.meStore.checkRole("isCompanyUser")) {
                setDelayLoading(true);
                await new Promise(resolve => setTimeout(resolve, loadWithDelay));
                onChange(response?.rows?.[0]?._id);
                setDelayLoading(false);
            }
        }
    });

    useEffect(() => {
        if (subCompanies?.rows) {
            const filteredSubCompanies = subCompanies.rows.filter(optionFilter);
            onFilteredCountChange(filteredSubCompanies.length);
        }
    }, [subCompanies, optionFilter, onFilteredCountChange]);

    const normalizeText = text => {
        if (!text) return "";

        let normalized = text.toLowerCase();

        normalized = turkishToEnglish(normalized);

        normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        return normalized;
    };

    const onSelect = value => {
        onChange(value);
        onSelectedSubCompaniesChange(
            subCompanies?.rows?.filter(subCompany => value?.includes(subCompany._id))
        );
    };
    return (
        <React.Fragment>
            {showLabel && <h5>{t("fields.subcompany", {ns: "common"})}</h5>}
            <SelectField
                showSearch
                loading={subCompaniesLoading || delayLoading}
                data-testid="subCompanySelect"
                value={value}
                onChange={onSelect}
                allowClear={allowClear}
                disabled={
                    disabled &&
                    (!company || company === "all" || company === "pool" || subCompaniesLoading)
                }
                mode={mode}
                filterOption={(input, option) =>
                    normalizeText(option.children).indexOf(normalizeText(input)) >= 0 ||
                    normalizeText(option.value).indexOf(normalizeText(input)) >= 0
                }
                mb={marginBottom}
                {...props}
            >
                {showAllOption && (
                    <SelectField.Option value="all" data-testid="option">
                        {t("filters.all", {ns: "common"})}
                    </SelectField.Option>
                )}
                {subCompanies?.rows &&
                    subCompanies?.rows.length > 0 &&
                    arrayAlphabeticalSort([...subCompanies?.rows], "name")
                        .filter(optionFilter)
                        .map(subCompany => {
                            const isOptionDisabled = optionDisabled(subCompany);
                            return (
                                <SelectField.Option
                                    key={subCompany._id}
                                    value={subCompany._id}
                                    disabled={isOptionDisabled}
                                    data-testid="subCompanySelect_option"
                                >
                                    {isOptionDisabled && optionDisabledTooltip ? (
                                        <Tooltip
                                            title={optionDisabledTooltip}
                                            getPopupContainer={() => document.body}
                                            overlayClassName="select-option-disabled-tooltip"
                                        >
                                            {/* Negatif margin + eşdeğer padding, hover alanını
                                                seçeneğin tüm satırına (yazının dışına da) yayar. */}
                                            <span
                                                style={{
                                                    display: "block",
                                                    margin: "-5px -12px",
                                                    padding: "5px 12px"
                                                }}
                                            >
                                                {subCompany.name}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        subCompany.name
                                    )}
                                </SelectField.Option>
                            );
                        })}
            </SelectField>
        </React.Fragment>
    );
};

export default SubCompanyInput;
